import { useMemo, useState } from 'react'
import { autoSnake, manualSnake } from './ggSnake.js'
import { samplePlayers } from './ggSamplePlayers.js'

// G-Snake — isolated sandbox. Auto-settle (ON): tap a 3-putt per hole; the app
// auto-tracks who holds the snake and settles the pot under the chosen model
// (last-holder-pays or fixed-per-3-putt). Manual (OFF): pick the holder yourself.
const cell = { border: '1px solid #ddd', padding: '6px 10px', fontSize: 16 }
const Seg = ({ options, value, onChange }) => (
  <div className="flex items-center gap-1 rounded-lg p-1" style={{ backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB' }}>
    {options.map((o) => {
      const on = value === o.value
      return <button key={String(o.value)} onClick={() => onChange(o.value)} className="rounded-md px-3 py-1 text-xs font-bold uppercase tracking-wider transition"
        style={on ? { backgroundColor: '#1B4D2E', color: '#fff' } : { backgroundColor: 'transparent', color: '#374151' }}>{o.label}</button>
    })}
  </div>
)

export default function GSnake() {
  const players = useMemo(() => samplePlayers().slice(0, 4), [])
  const [auto, setAuto] = useState(true)
  const [amount, setAmount] = useState(5)
  const [model, setModel] = useState('last')
  const [marks, setMarks] = useState(Array(18).fill('')) // per-hole 3-putt playerId or ''
  const [mHolder, setMHolder] = useState('')

  const markers = useMemo(() => marks.map((pid, hole) => (pid ? { hole, playerId: pid } : null)).filter(Boolean), [marks])
  const r = useMemo(
    () => (auto ? autoSnake(players, markers, Number(amount) || 0, { model }) : manualSnake(players, mHolder || null, Number(amount) || 0)),
    [auto, players, markers, amount, model, mHolder],
  )
  const name = (id) => (id ? players.find((p) => p.id === id)?.name || id : '—')

  return (
    <div className="card">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <Seg options={[{ value: true, label: 'Auto-settle' }, { value: false, label: 'Manual' }]} value={auto} onChange={setAuto} />
        <span className="text-sm text-gray-600 font-semibold">Pot $<input type="number" className="w-14 border rounded px-1 py-0.5 ml-1" value={amount} onChange={(e) => setAmount(e.target.value)} /></span>
        {auto && <Seg options={[{ value: 'last', label: 'Last pays' }, { value: 'fixed', label: 'Fixed/3-putt' }]} value={model} onChange={setModel} />}
      </div>

      {auto ? (
        <div className="mb-3">
          <div className="text-sm text-gray-500 mb-1">Tap the 3-putter on each hole:</div>
          <div className="flex flex-wrap gap-1">
            {marks.map((pid, h) => (
              <label key={h} className="flex flex-col items-center text-[10px] text-gray-500">
                <span>{h + 1}</span>
                <select className="border rounded px-0.5 py-0.5 text-[11px]" value={pid} onChange={(e) => setMarks((m) => m.map((x, j) => (j === h ? e.target.value : x)))}>
                  <option value="">–</option>
                  {players.map((p) => <option key={p.id} value={p.id}>{p.name.split(' ')[0]}</option>)}
                </select>
              </label>
            ))}
          </div>
        </div>
      ) : (
        <div className="mb-3 text-xs"><label className="font-semibold text-gray-600">Snake holder <select className="border rounded px-1 py-0.5 ml-1" value={mHolder} onChange={(e) => setMHolder(e.target.value)}><option value="">— none —</option>{players.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label></div>
      )}

      <p className="text-sm mb-2">🐍 Held by <b>{name(r.holder)}</b>{auto ? ` · ${model === 'fixed' ? 'fixed per 3-putt' : 'last pays'}` : ''}</p>
      <table className="w-full text-base" style={{ borderCollapse: 'collapse' }}>
        <thead><tr style={{ background: '#f6f6f2', color: '#555' }}>{['Player', 'Settlement'].map((h) => <th key={h} style={{ ...cell, textAlign: 'left' }}>{h}</th>)}</tr></thead>
        <tbody>{players.map((p) => {
          const v = r.payouts[p.id] || 0
          return <tr key={p.id}><td style={cell}>{p.name}</td><td style={cell}><b style={{ color: v >= 0 ? '#1B7A3E' : '#B42318' }}>{v >= 0 ? '+' : '−'}${Math.abs(v)}</b></td></tr>
        })}</tbody>
      </table>
    </div>
  )
}
